import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAppointment,
  getStaffForPatient,
  updateAppointment,
} from "../services/api";

// Helper function to convert HH:MM:SS to minutes
const timeStringToMinutes = (timeString) => {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return hours * 60 + minutes + seconds / 60;
};

// Helper function to convert minutes to HH:MM:SS
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.round((minutes % 1) * 60);
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const AppointmentEdit = ({ authToken }) => {
  const { patientId, appointmentId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState({
    date_time: "",
    type: "",
    status: "",
    staff_id: "",
    duration: "",
  });
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appointmentData, staffData] = await Promise.all([
          getAppointment(authToken, patientId, appointmentId),
          getStaffForPatient(authToken, patientId),
        ]);
        setAppointment({
          ...appointmentData,
          staff_id: appointmentData.staff ? appointmentData.staff.id : "",
          duration: appointmentData.duration
            ? timeStringToMinutes(appointmentData.duration)
            : "",
        });

        console.log(appointmentData);
        setStaffList(staffData);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch appointment data. Please try again.");
        setLoading(false);
      }
    };
    fetchData();
  }, [authToken, patientId, appointmentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAppointment((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateAppointment(authToken, patientId, appointmentId, appointment);
      navigate(`/patients/${patientId}/appointments`);
    } catch (err) {
      setError("Failed to update appointment. Please try again.");
    }
  };

  if (loading)
    return <p className="text-center py-4">Loading appointment...</p>;
  if (error) return <p className="text-center py-4 text-red-500">{error}</p>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-xl font-bold mb-4">Edit Appointment</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="date_time"
            className="block text-sm font-medium text-gray-700"
          >
            Date & Time
          </label>
          <input
            type="datetime-local"
            id="date_time"
            name="date_time"
            value={appointment.date_time}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            required
          />
        </div>
        <div>
          <label
            htmlFor="type"
            className="block text-sm font-medium text-gray-700"
          >
            Type
          </label>
          <input
            type="text"
            id="type"
            name="type"
            value={appointment.type}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            required
          />
        </div>
        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700"
          >
            Status
          </label>
          <select
            id="status"
            name="status"
            value={appointment.status}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            required
          >
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="staff_id"
            className="block text-sm font-medium text-gray-700"
          >
            Staff
          </label>
          <select
            id="staff_id"
            name="staff_id"
            value={appointment.staff_id}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            required
          >
            <option value="">Select staff</option>
            {staffList.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.user.username} - {staff.role.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="duration"
            className="block text-sm font-medium text-gray-700"
          >
            Duration (minutes)
          </label>
          <input
            type="text"
            id="duration"
            name="duration"
            value={appointment.duration}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <button
            type="submit"
            className="px-4 py-2 bg-btn-primary text-white rounded hover:bg-btn-primary-hover"
          >
            Update Appointment
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentEdit;
